import React, { useEffect, useRef, useState } from "react";
import { api, formatErr, resolveImage, API } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Card, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Search, Upload, FileUp, X as XIcon, ImageIcon } from "lucide-react";

const UNITS = ["Liter", "KG", "Gram", "Packet", "Piece"];
const EMPTY = { product_name: "", product_code: "", unit: "Liter", description: "", image_url: "", min_stock_threshold: 0, items_per_crate: 24, status: true };

export default function Products() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const importRef = useRef(null);

  const load = () => api.get("/products").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const filtered = list.filter(p =>
    !q || p.product_name.toLowerCase().includes(q.toLowerCase()) ||
    p.product_code.toLowerCase().includes(q.toLowerCase())
  );

  function openCreate() { setEditing(null); setForm(EMPTY); setOpen(true); }
  function openEdit(p) { setEditing(p); setForm({ ...EMPTY, ...p }); setOpen(true); }

  async function uploadImage(file) {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("ddms_token");
      const res = await fetch(`${API}/files/upload`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      setForm(f => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally { setUploading(false); }
  }

  async function bulkImport(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("ddms_token");
      const res = await fetch(`${API}/products/bulk-import`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Import failed");
      toast.success(`Imported — ${data.created} created, ${data.updated} updated`);
      load();
    } catch (e) {
      toast.error(e.message || "Import failed");
    }
  }

  async function save() {
    try {
      const payload = { ...form, min_stock_threshold: Number(form.min_stock_threshold) || 0 };
      if (editing) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }
  async function remove(p) {
    if (!window.confirm(`Delete ${p.product_name}?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  return (
    <div data-testid="products-page">
      <PageHeader
        eyebrow="Catalog"
        title="Products"
        description="Manage dairy products, images, units, SKUs and stock thresholds."
        actions={
          <div className="flex gap-2">
            <input type="file" accept=".csv" ref={importRef} className="hidden" onChange={(e)=>{ const f=e.target.files?.[0]; if(f) bulkImport(f); e.target.value=""; }}/>
            <Button variant="outline" onClick={()=>importRef.current?.click()} data-testid="product-bulk-import-btn">
              <FileUp size={14} className="mr-1"/>Bulk Import (CSV)
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreate} className="bg-cobalt-500 hover:bg-cobalt-700 text-white" data-testid="product-create-btn"><Plus size={16} className="mr-1"/>New Product</Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl" data-testid="product-dialog">
                <DialogHeader><DialogTitle>{editing ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 flex items-start gap-4">
                    <div className="w-24 h-24 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                      {form.image_url ? (
                        <img src={resolveImage(form.image_url)} alt="preview" className="w-full h-full object-cover" data-testid="product-image-preview"/>
                      ) : (
                        <ImageIcon size={26} className="text-slate-300"/>
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Product Image</Label>
                      <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={(e)=>{ const f=e.target.files?.[0]; if(f) uploadImage(f); e.target.value=""; }}/>
                      <div className="flex flex-wrap gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={()=>fileRef.current?.click()} disabled={uploading} data-testid="product-image-upload-btn">
                          <Upload size={12} className="mr-1"/>{uploading ? "Uploading…" : (form.image_url ? "Change Image" : "Upload Image")}
                        </Button>
                        {form.image_url && (
                          <Button type="button" size="sm" variant="outline" className="text-rose-600 border-rose-200" onClick={()=>setForm({...form, image_url: ""})} data-testid="product-image-delete-btn">
                            <XIcon size={12} className="mr-1"/>Remove
                          </Button>
                        )}
                      </div>
                      <Input placeholder="…or paste image URL" value={form.image_url} onChange={(e)=>setForm({...form, image_url: e.target.value})} data-testid="product-image-url"/>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Label>Name</Label>
                    <Input data-testid="product-name" value={form.product_name} onChange={e=>setForm({...form, product_name: e.target.value})}/>
                  </div>
                  <div>
                    <Label>Code</Label>
                    <Input data-testid="product-code" value={form.product_code} onChange={e=>setForm({...form, product_code: e.target.value})}/>
                  </div>
                  <div>
                    <Label>Unit</Label>
                    <Select value={form.unit} onValueChange={(v)=>setForm({...form, unit: v})}>
                      <SelectTrigger data-testid="product-unit"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Min Stock Threshold</Label>
                    <Input type="number" step="0.01" data-testid="product-min-stock" value={form.min_stock_threshold} onChange={e=>setForm({...form, min_stock_threshold: e.target.value})}/>
                  </div>
                  <div>
                    <Label>Crate Capacity (Items/Crate)</Label>
                    <Input type="number" step="1" placeholder="e.g. 24 for milk, 12 for curd" value={form.items_per_crate} onChange={e=>setForm({...form, items_per_crate: e.target.value})}/>
                  </div>
                  <div className="col-span-2">
                    <Label>Description</Label>
                    <Textarea rows={2} value={form.description} onChange={e=>setForm({...form, description: e.target.value})}/>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                  <Button onClick={save} className="bg-cobalt-500 hover:bg-cobalt-700" data-testid="product-save-btn">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <Card padding="p-0">
        <div className="p-4 border-b border-slate-200">
          <div className="relative max-w-sm">
            <Search size={14} className="absolute left-3 top-3 text-slate-400"/>
            <Input data-testid="product-search" placeholder="Search by name or code…" className="pl-9" value={q} onChange={e=>setQ(e.target.value)}/>
          </div>
        </div>
        {filtered.length === 0 ? <div className="p-8"><Empty title="No products" description="Create your first product or bulk import via CSV."/></div> : (
          <div className="overflow-x-auto">
            <table className="ddms-table" data-testid="products-table">
              <thead>
                <tr><th style={{width: 64}}></th><th>Name</th><th>Code</th><th>Unit</th><th className="num">Stock</th><th className="num">Min</th><th className="num">Items/Crate</th><th>Description</th><th className="num">Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map(p => (
                  <tr key={p.id}>
                    <td>
                      {p.image_url ? (
                        <img src={resolveImage(p.image_url)} alt={p.product_name} className="w-12 h-12 rounded-md object-cover border border-slate-200" />
                      ) : (
                        <div className="w-12 h-12 rounded-md border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-[10px] text-slate-400">no img</div>
                      )}
                    </td>
                    <td className="font-medium text-slate-900">{p.product_name}</td>
                    <td className="font-mono text-xs">{p.product_code}</td>
                    <td>{p.unit}</td>
                    <td className="num font-mono">{(p.current_stock ?? 0).toFixed(2)}</td>
                    <td className="num text-slate-500 font-mono">{(p.min_stock_threshold ?? 0).toFixed(2)}</td>
                    <td className="num font-mono font-semibold text-cobalt-700">{p.items_per_crate || 24}</td>
                    <td className="text-slate-500">{p.description}</td>
                    <td className="num">
                      <Button size="sm" variant="outline" onClick={()=>openEdit(p)} className="mr-2" data-testid={`product-edit-${p.product_code}`}><Pencil size={12}/></Button>
                      <Button size="sm" variant="outline" onClick={()=>remove(p)} className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 size={12}/></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
